import React from 'react'
import { DayResult, TransactionOccurrence } from '../types'
import Modal from './Modal'
import { formatMoney, formatSigned } from '../lib/format'
import { ragBg, ragText, ragLabel } from '../lib/ragStyles'
import { fromISODate } from '../lib/dateRules'

interface Props {
  iso: string
  result?: DayResult
  currencySymbol: string
  onClose: () => void
  onAddOneOff: (iso: string) => void
}

/** Keyed by every kind, so a new one can't reach the list without a label. */
const KIND_LABEL: Record<TransactionOccurrence['sourceKind'], string> = {
  income: 'Income',
  expense: 'Bill',
  budget: 'Budget',
  oneoff: 'One-off',
  transfer: 'Transfer',
  adjustment: 'Adjustment',
  interest: 'Interest',
}

export default function DayDetailModal({ iso, result, currencySymbol, onClose, onAddOneOff }: Props) {
  const date = fromISODate(iso)
  const openingBalance = result ? result.balance - result.netChange : undefined
  const dateLabel = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <Modal title={dateLabel} onClose={onClose}>
      {!result ? (
        <p className="text-sm text-ink/75">
          No forecast data for this day yet — it may be before your starting balance date.
        </p>
      ) : (
        <>
          <div className={`rounded p-3 mb-4 flex items-center justify-between ${ragBg[result.rag]}`}>
            <div>
              <div className="text-xs text-ink/75 font-sans">Closing balance</div>
              <div className={`text-2xl font-mono font-medium font-tabular ${ragText[result.rag]}`}>
                {formatMoney(result.balance, currencySymbol)}
              </div>
            </div>
            <span className={`text-xs font-sans px-2 py-1 rounded-sm bg-white/60 ${ragText[result.rag]}`}>
              {ragLabel[result.rag]}
            </span>
          </div>

          <div className="text-xs font-mono text-ink/70 mb-2 font-tabular">
            Opening {formatMoney(openingBalance ?? 0, currencySymbol)}
          </div>

          {result.targetHits.map((hit) => (
            <div
              key={hit.targetId}
              className="rounded-sm bg-gold-bg ring-1 ring-gold px-3 py-2 mb-2 text-sm text-gold-deep"
            >
              Target reached — <span className="font-medium">{hit.name}</span> at{' '}
              <span className="font-mono font-tabular">{formatMoney(hit.level, currencySymbol)}</span>
            </div>
          ))}

          {result.transactions.length === 0 ? (
            <p className="text-sm text-ink/70 mb-4">No transactions on this day.</p>
          ) : (
            <ul className="divide-y divide-line mb-4 border-y border-line">
              {result.transactions.map((tx, i) => (
                <li key={i} className="py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-ink">{tx.name}</div>
                      <div className="text-xs text-ink/70 font-sans">
                        {tx.accountName ? `${tx.accountName} · ` : ''}
                        {KIND_LABEL[tx.sourceKind]}
                        {tx.category ? ` · ${tx.category}` : ''}
                        {tx.isActual && tx.budgeted !== undefined
                          ? ` · actual, ${tx.sourceKind === 'expense' ? 'planned' : tx.sourceKind === 'income' ? 'expected' : 'budgeted'} ${formatMoney(tx.budgeted, currencySymbol)}`
                          : ''}
                      </div>
                    </div>
                    <div className={`font-mono font-tabular ${tx.amount < 0 ? 'text-rag-bad' : 'text-rag-good'}`}>
                      {formatSigned(tx.amount, currencySymbol)}
                    </div>
                  </div>
                  {tx.notes && <div className="text-xs text-ink/70 font-sans mt-1">{tx.notes}</div>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <button
        onClick={() => onAddOneOff(iso)}
        className="text-sm font-sans text-accent hover:underline"
      >
        + Add a one-off transaction on this day
      </button>
    </Modal>
  )
}
